import { useEffect, useMemo, useState, useRef } from "react";
import { AbsDropDown, Button, COLORS, Input, Label, Modal } from "abs-ui-components";
import { useSelector } from "react-redux";
import { Accordion, AccordionDetails, AccordionSummary, Tooltip } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "./CIIVerifiedInput.scss";
import axiosHttp from "../../../../../services/interceptor";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import Loader from "../../../../../common/Loader/Loader";
import { CANCEL, DELETE, DELETE_CONFIRMATION_TEXT_FOR_SINGLE_RECORD } from "../../../../../constants/CommonConstants";
import { ExpandMore } from "@mui/icons-material";
import { buildSavePayload, emptyPeriod, getVerifiedInputCii, hydrateFromApi, removeVerifiedCii, saveVerifiedCii } from "./CIIVerifiedInputHelpers";
import ReportingPeriod from "./ReportingPeriod";
import { CORRECTION_FIELDS, FUEL_FIELDS, REPORT_FIELDS } from "./FieldConfigs";

const DATE_RANGE_ERRORS = {
    reportingStart: "Reporting Start cannot be later than Reporting End.",
    reportingEnd: "Reporting End cannot be earlier than Reporting Start.",
};

const REPORTING_YEAR_ERROR = "Reporting dates must fall within the reporting year.";
const OVERLAP_ERROR = "The selected reporting period overlaps with an existing verified reporting period";
const DCS_OVERLAP_ERROR = "The selected reporting period overlaps with the vessel's reported DCS period.";

const CIIVerifiedInput = ({ onChange }) => {

    const reportDetails = useSelector((state) => state?.reports?.selectedReportDetails);
    const reportMstId = reportDetails?.ReportMstId;
    const reportingYear = Number(reportDetails?.ReportYear) || null;
    const [periods, setPeriods] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isEditable, setIsEditable] = useState(false);
    const [rowErrors, setRowErrors] = useState({});
    // Last saved reporting dates of each period: { [period.id]: { start, end } }
    const savedRangesRef = useRef({});
    // State for the remove confirmation modal
    const [confirmRemove, setConfirmRemove] = useState({ open: false, periodId: null });
    const [expandedPanel, setExpandedPanel] = useState(null);
    const [hasAutoOpened, setHasAutoOpened] = useState(false);
    const handleAccordionChange = (panelId) => (event, isExpanded) => {
        setExpandedPanel(isExpanded ? panelId : null);
        setHasAutoOpened(true);
    };
    useEffect(() => {
        if (!hasAutoOpened && periods.length > 0) {
            setExpandedPanel(periods[0].id);
            setHasAutoOpened(true);
        }
    }, [periods, hasAutoOpened]);
    const isEmpty = (v) => v === "" || v == null;
    const isNegativeValue = (v) => v !== "" && v != null && Number(v) < 0;
    const hasNegativeValues = (period) => {
        const check = (val) => isNegativeValue(val);

        // Top-level fields
        if (REPORT_FIELDS.some(f => check(period[f.name]))) return true;

        // Fuel rows
        if ((period.fuelConsumptions ?? []).some(row =>
            FUEL_FIELDS.some(f => check(row[f.name]))
        )) return true;

        // Correction rows
        if ((period.correctionFactors ?? []).some(row =>
            CORRECTION_FIELDS.some(f => check(row[f.name]))
        )) return true;

        return false;
    };
    
    // Date validation: reporting year first, then start <= end
    const getDateErrors = (period) => {
        const errs = {};
        const { reportingStart, reportingEnd } = period;
        if (isEmpty(reportingStart) || isEmpty(reportingEnd)) return errs;

        const start = dayjs(reportingStart);
        const end = dayjs(reportingEnd);
        if (!start.isValid() || !end.isValid()) return errs;

        // 1) Reporting year check (takes priority, field by field)
        if (reportingYear) {
            if (start.year() !== reportingYear) errs.reportingStart = REPORTING_YEAR_ERROR;
            if (end.year() !== reportingYear) errs.reportingEnd = REPORTING_YEAR_ERROR;
        } else if (start.year() !== end.year()) {
            errs.reportingStart = REPORTING_YEAR_ERROR;
            errs.reportingEnd = REPORTING_YEAR_ERROR;
        }

        // 2) Start later than End (only on fields that don't already have the year error)
        if (start.isAfter(end, "day")) {
            if (!errs.reportingStart) errs.reportingStart = DATE_RANGE_ERRORS.reportingStart;
            if (!errs.reportingEnd) errs.reportingEnd = DATE_RANGE_ERRORS.reportingEnd;
        }
        return errs;
    };

                  // Returns the period numbers (1, 2, 3...) of saved periods that have the same start and end dates
    const getOverlappingPeriodNumbers = (period) => {
        if (isEmpty(period.reportingStart) || isEmpty(period.reportingEnd)) return [];
        const start = dayjs(period.reportingStart);
        const end = dayjs(period.reportingEnd);
        const numbers = [];
        periods.forEach((p, i) => {
            if (p.id === period.id) return;                       // skip itself
            const saved = savedRangesRef.current[p.id];           // last SAVED dates of that period
            if (!saved || isEmpty(saved.start) || isEmpty(saved.end)) return;   // never saved
            if (start.isSame(dayjs(saved.start), "day") && end.isSame(dayjs(saved.end), "day")) numbers.push(i + 1);
        });
        return numbers;
    };

    const validatePeriod = (period) => {
        const errors = {};

        // ---- top-level report fields ----
         REPORT_FIELDS.forEach((f) => {
        const v = period[f.name];
        if (f.required && isEmpty(v)) {
            errors[f.name] = `${f.label} is required`;
        }
    });

   // ---- reporting date range / reporting year ----
        Object.assign(errors, getDateErrors(period));

        (period.fuelConsumptions ?? []).forEach((row, i) => {
        if (!row.fuelType) {
            errors[`fuelConsumptions[${i}].fuelType`] = "Fuel Type is required";
            return;
        }
        FUEL_FIELDS.forEach((f) => {
            const v = row[f.name];
            if (f.required && isEmpty(v)) {
                errors[`fuelConsumptions[${i}].${f.name}`] =
                    `${f.label} is required`;
            }
        });
    });

        (period.correctionFactors ?? []).forEach((row, i) => {
        if (!row.fuelType) {
            errors[`correctionFactors[${i}].fuelType`] = "Fuel Type is required";
            return;
        }
        CORRECTION_FIELDS.forEach((f) => {
            const v = row[f.name];
            if (f.required && isEmpty(v)) {
                errors[`correctionFactors[${i}].${f.name}`] =
                    `${f.label} is required`;
            }
        });
    });

        return errors;
    };

    const handleConfirmCII = async (periodId) => {
        const period = periods.find((p) => p.id === periodId);
        if (!period) return;
        const errors = validatePeriod(period);
        const hasNegatives = hasNegativeValues(period); 
        if (Object.keys(errors).length > 0 || hasNegatives   ) {
            setRowErrors((prev) => ({ ...prev, [periodId]: errors }));
            return;
        }


               // verified reporting periods must not overlap
        const overlapping = getOverlappingPeriodNumbers(period);
        if (overlapping.length > 0) {
            emitToasterMsg("error", "Verified CII Data", `${OVERLAP_ERROR} ${overlapping.join(", ")}.`);
            return;
        }

        setRowErrors((prev) => ({ ...prev, [periodId]: {} }));
        try {
            setSaving(true);
            const payload = buildSavePayload([period]);
            const res = await saveVerifiedCii(reportMstId, payload);
                        if (res?.overlapsDcsPeriod) {
                emitToasterMsg("error", "Verified CII Data", DCS_OVERLAP_ERROR);
                return;
            }
            const refreshedData = await getVerifiedInputCii(reportMstId);
            const mapped = hydrateFromApi(refreshedData);
            setPeriods(mapped);
            savedRangesRef.current[periodId] = { start: period.reportingStart, end: period.reportingEnd };
            onChange?.({ saved: true, response: res });
            setPeriods((prev) =>
                prev.map((p) =>
                    p.id === periodId ? { ...p, confirmedAt: Date.now(), dirty: false } : p
                )
            );
            emitToasterMsg("success", "Verified CII Data", "Verified CII data saved successfully.");
        } catch (error) {
            console.error("Error saving verified CII data:", error);
            emitToasterMsg("error", "Verified CII Data", "Failed to save verified CII data. Please try again.");
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => {
        if (!reportMstId) {
            setLoading(false);
            return;
        }
        (async () => {
            try {
                setLoading(true);
                const res = await getVerifiedInputCii(reportMstId);
                setIsEditable(!!res.isEditable);
                const mapped = hydrateFromApi(res);

                savedRangesRef.current = {};
                mapped.forEach((p) => {
                    savedRangesRef.current[p.id] = { start: p.reportingStart, end: p.reportingEnd };
                });

                setPeriods(mapped);
            } catch (err) {
                console.error(" GET failed:", err?.response?.data ?? err);
            }
            finally {
                setLoading(false);
            }
        })();
    }, [reportMstId]);

    const addPeriod = () => {
        const newPeriod = emptyPeriod(periods.length);
        setPeriods((prev) => [...prev, newPeriod]);
        setExpandedPanel(newPeriod.id);
    };
    const removePeriod = (localId) => {
        const period = periods.find((p) => p.id === localId);
        if (!period) return;
        if (period.periodId != null) {
            setConfirmRemove({ open: true, localId });
            return;
        }
        if (!hasAnyData(period)) {
            setPeriods((prev) =>
                prev
                    .filter((p) => p.id !== localId)
                    .map((p, i) => ({
                        ...p,
                        periodLabel: `VERIFIED REPORTING PERIOD ${i + 1}`,
                    }))
            );
            return;
        }
        setConfirmRemove({ open: true, localId });
    };

    const handleRemoveConfirmed = async () => {
        const { localId } = confirmRemove;
        setConfirmRemove({ open: false, localId: null });

        const period = periods.find((p) => p.id === localId);
        if (!period) return;

        try {
            setSaving(true);

            // Call the backend ONLY if the period was saved (has a backend id)
            if (period.periodId != null) {
                const res = await removeVerifiedCii(reportMstId, period.periodId);
                onChange?.({ removed: true, response: res });
            }

            delete savedRangesRef.current[localId];

            // Always drop it locally
            setPeriods((prev) =>
                prev
                    .filter((p) => p.id !== localId)
                    .map((p, i) => ({
                        ...p,
                        periodLabel: `VERIFIED REPORTING PERIOD ${i + 1}`,
                    }))
            );

            // close the accordion if this was the open one
            if (expandedPanel === localId) setExpandedPanel(null);

            emitToasterMsg("success", "Verified CII Data", "Verified CII data removed successfully.");
        } catch (error) {
            console.error("Error removing verified CII data:", error);
            emitToasterMsg("error", "Verified CII Data", "Failed to remove verified CII data. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const updateReport = (periodId, name, val) => {
        setPeriods((prev) =>
            prev.map((p) =>
                p.id === periodId
                    ? { ...p, [name]: val, dirty: p.confirmedAt ? true : false }
                    : p
            )
        );
                       setRowErrors((prev) => {
            const pErr = prev[periodId];
            const isDateField = name === "reportingStart" || name === "reportingEnd";
            if (!isDateField && !pErr?.[name]) return prev;
            const next = { ...(pErr ?? {}) };
            delete next[name];

            // validate the dates immediately against the new value
            if (isDateField) {
                const current = periods.find((p) => p.id === periodId);
                const live = getDateErrors({ ...current, [name]: val });
                ["reportingStart", "reportingEnd"].forEach((k) => {
                    if (live[k]) next[k] = live[k];
                    else if (next[k] === DATE_RANGE_ERRORS[k] || next[k] === REPORTING_YEAR_ERROR) delete next[k];
                });
            }
            return { ...prev, [periodId]: next };
        });

        onChange?.({ periodId, name, value: val });
    };

    const updateRow = (periodId, key, rowIndex, name, val) => {
        setPeriods((prev) =>
            prev.map((p) => {
                if (p.id !== periodId) return p;
                const rows = [...(p[key] ?? [])];
                rows[rowIndex] = { ...rows[rowIndex], [name]: val };
                return { ...p, [key]: rows, dirty: p.confirmedAt ? true : false };
            })
        );
        setRowErrors((prev) => {
            const errKey = `${key}[${rowIndex}].${name}`;
            const pErr = prev[periodId];
            if (!pErr?.[errKey]) return prev;
            const next = { ...pErr };
            delete next[errKey];
            return { ...prev, [periodId]: next };
        });
    };

    const addRowToPeriod = (periodId, key, factory) => {
        setPeriods((prev) =>
            prev.map((p) =>
                p.id === periodId
                    ? { ...p, [key]: [...(p[key] ?? []), factory()], dirty: p.confirmedAt ? true : false }
                    : p
            )
        );
    };

    const removeRowFromPeriod = (periodId, key, rowIndex) => {
        setPeriods((prev) =>
            prev.map((p) =>
                p.id === periodId
                    ? { ...p, [key]: (p[key] ?? []).filter((_, i) => i !== rowIndex), dirty: p.confirmedAt ? true : false }
                    : p
            )
        );
    };

    const hasAnyData = (period) => {
        const top =
            period.reportingStart ||
            period.reportingEnd ||
            period.distanceTravelled ||
            period.distanceExcluded;

        const fuel = (period.fuelConsumptions ?? []).some(
            (r) => r.fuelType || r.cf || r.amount 
        );

        const corr = (period.correctionFactors ?? []).some(
            (r) =>
                r.fuelType ||
                r.cf ||
                r.fcVoyage ||
                r.tfj ||
                r.fcElectrical ||
                r.fcBoiler ||
                r.fcOther
        );

        return !!(top || fuel || corr);
    };
    if (loading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: 200 }}
            >
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden"></span>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-2">
            {/* Full-screen loader while an API call is in progress */}
            {saving && <Loader />}
            <div className="d-flex align-items-center mb-3">
                <Label
                    name="Input of Verified CII Data due to Change of Management / Change of Flag"
                    bold
                    size="md"
                />
                <Tooltip
                    title={
                        !isEditable
                            ? "DCS Reporting period must end on Dec 31 to add another period"
                            : undefined
                    }
                    placement="top"
                    arrow
                >
                    <span style={{ display: "inline-block", marginLeft: 16 }}>
                        <Button
                            onClick={addPeriod}
                            disabled={!isEditable}
                        >
                            + Add Additional Reporting Period
                        </Button>
                    </span>
                </Tooltip>
            </div>

            {/* All reporting periods */}
            {periods.map((period, index) => (
                <ReportingPeriod
                    key={period.id}
                    period={period}
                    index={index}
                    totalPeriods={periods.length}
                    onRemovePeriod={removePeriod}
                    onChangeReport={updateReport}
                    onChangeRow={updateRow}
                    onAddRow={addRowToPeriod}
                    onRemoveRow={removeRowFromPeriod}
                    handleConfirmCII={handleConfirmCII}
                    disabled={!isEditable}
                    saving={saving}
                    errors={rowErrors[period.id] ?? {}}
                    expandedPanel={expandedPanel}
                    onAccordionChange={handleAccordionChange}
                />
            ))}

            {/* Confirmation modal for removing a verified CII period */}
            <Modal
                isOpen={confirmRemove.open}
                modalHeader
                padding={"70px"}
                headerHeight={"50px"}
                showFooter
                onCloseClickListener={() => setConfirmRemove({ open: false, periodId: null })}
                footer={
                    <div className="d-flex justify-content-end fixed-action-btns cii-input  p-4">
                        <Button
                            onClick={handleRemoveConfirmed}
                            primaryColor={COLORS.RED.DARK}
                            primaryBackgroundColor={COLORS.RED.LIGHT}
                        >
                            {DELETE}
                        </Button>
                        <Button
                            className="ml-2"
                            onClick={() => setConfirmRemove({ open: false, periodId: null })}
                        >
                            {CANCEL}
                        </Button>
                    </div>
                }
            >
                <div>{DELETE_CONFIRMATION_TEXT_FOR_SINGLE_RECORD}</div>
            </Modal>

        </div>
    );
};

export default CIIVerifiedInput;